import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { OnboardingStep } from '../../shared/models/onboarding.model';
import { AuthUser, PaymentType, Role } from '../../shared/models/user.model';
import { LegalDocumentsService, UserClickwrapDetail, UserClickwrapStatus } from '../../shared/services/legal-documents.service';
import { UsersService } from '../../shared/services/users.service';

@Component({
  standalone: false,
  selector: 'app-onboarding',
  templateUrl: './onboarding.component.html',
  styleUrls: ['./onboarding.component.scss'],
})
export class OnboardingComponent implements OnInit {
  private readonly usersService = inject(UsersService);
  private readonly legalDocumentsService = inject(LegalDocumentsService);
  private readonly router = inject(Router);
  private readonly progressRadius = 26;

  readonly currentUser = signal<AuthUser | null>(null);
  readonly isBillingAvailableForUser = signal<boolean | null>(null);
  readonly allClickwrapTemplatesAccepted = signal<boolean | null>(null);
  readonly steps = signal<OnboardingStep[]>([
    {
      title: 'Trial Account Activation',
      status: 'Under review',
      state: 'under-review',
      description: 'An account Manager will review your request and activate your trial.',
      step: 'account-activation',
    },
    {
      title: 'Company Details',
      description:
        'Provide your company and billing details (e.g., address). <strong>Please ensure your details are correct before proceeding.</strong> Invoices will be issued based on this information, and any inaccuracies may lead to processing issues or delays.',
      state: 'in-progress',
      route: '/users/account',
      step: 'company-details',
    },
    {
      title: 'Legal Documents',
      description: 'Once your company details are submitted and reviewed, your agreement will become available for you to review and sign.',
      state: 'pending',
      route: '/legal-documents',
      step: 'legal-documents',
    },
  ]);

  readonly shouldOnboardingBeShown = computed(() => {
    const user = this.currentUser();
    return !!user && (!user.enabled || !user.isOnboarded) && user.role === Role.MAIN && !user.accountRegisteredBeforeClickwrap;
  });

  readonly isPostpaid = computed(() => this.currentUser()?.paymentType === PaymentType.POSTPAID);
  readonly billingUnavailableMessage = computed(() => (this.isPostpaid() ? '' : 'Billing and top-up is currently unavailable. '));
  readonly isAgreementActionRequired = computed(() => {
    const user = this.currentUser();

    if (!user) return false;

    if (this.isPostpaid()) {
      return !!user.clicktermAgreementFlowEnabled && !user.customAgreement && this.allClickwrapTemplatesAccepted() === false;
    }

    return this.isBillingAvailableForUser() === false;
  });

  readonly shouldShowBillingRestrictionsForSubAccounts = computed(() => {
    const user = this.currentUser();

    if (!user) return false;

    const isSubAccount = [Role.DEPUTY, Role.SUB].includes(user.role);

    return !this.isPostpaid() && this.isBillingAvailableForUser() === false && user.enabled && isSubAccount;
  });
  readonly shouldShowBillingRestrictionsForNewMainAccount = computed(() => {
    const user = this.currentUser();

    if (!user) return false;

    const isMainAccount = user.role === Role.MAIN;

    return (
      !this.shouldOnboardingBeShown() &&
      this.isAgreementActionRequired() &&
      user.enabled &&
      isMainAccount &&
      !user.accountRegisteredBeforeClickwrap &&
      user.companyDetailsVerified
    );
  });
  readonly shouldShowCompanyDetailsUnderReviewForMainAccount = computed(() => {
    const user = this.currentUser();

    if (!user) return false;

    const isMainAccount = user.role === Role.MAIN;

    return (
      !this.shouldOnboardingBeShown() &&
      this.isAgreementActionRequired() &&
      user.enabled &&
      isMainAccount &&
      !user.accountRegisteredBeforeClickwrap &&
      !user.companyDetailsVerified
    );
  });
  readonly shouldShowFinishStepsForExistingMainAccounts = computed(() => {
    const user = this.currentUser();

    if (!user) return false;

    const isMainAccount = user.role === Role.MAIN;

    return (
      !this.shouldOnboardingBeShown() &&
      (this.isAgreementActionRequired() || !user.companyDetailsVerified) &&
      user.enabled &&
      isMainAccount &&
      user.accountRegisteredBeforeClickwrap &&
      user.clicktermAgreementFlowEnabled
    );
  });

  readonly completedSteps = computed(() => this.steps().filter(step => step.state === 'done').length);
  readonly totalSteps = computed(() => this.steps().length);
  readonly progressLabel = computed(() => `${this.completedSteps()}/${this.totalSteps()}`);

  readonly stepsForExistingUsers = computed(() => {
    const user = this.currentUser();
    if (!user) return [];

    return this.steps()
      .filter(step => {
        // always remove the activation step (index 0) for existing users
        if (step === this.steps()[0]) return false;

        // keep company details step only if not yet verified
        if (step === this.steps()[1]) return !user.companyDetailsVerified;

        // keep legal documents step only if an agreement action is required
        if (step === this.steps()[2]) return this.isAgreementActionRequired();

        return true;
      })
      .map(s => {
        if (s.step === 'company-details') {
          const isUnderReview = s.state === 'under-review';

          return {
            ...s,
            title: 'Confirm Your Company Details and DPO (Data Protection Officer) Email',
            description: isUnderReview
              ? "We're reviewing your company details. Once approved, you'll receive the MSA & DPA to review and sign."
              : 'Please review and confirm your company and billing information are up to date.',
          };
        }

        if (s.step === 'legal-documents') {
          return {
            ...s,
            title: 'Review and Accept the Agreement',
            description: 'Once your details are confirmed, the MSA & DPA will be available to review and sign.',
          };
        }

        return s;
      });
  });

  readonly progressDashArray = computed(() => {
    const circumference = 2 * Math.PI * this.progressRadius;
    const total = this.totalSteps();
    const ratio = total === 0 ? 0 : this.completedSteps() / total;
    const clamped = Math.min(Math.max(ratio, 0), 1);
    const active = Number((circumference * clamped).toFixed(2));
    const remaining = Number((circumference - active).toFixed(2));
    return `${active} ${remaining}`;
  });

  ngOnInit(): void {
    this.usersService.i().subscribe({
      next: user => {
        this.currentUser.set(user);
        this.getBillingAvailabilityForUser();
        this.configureSteps(user);
      },
    });
  }

  onStepSelect(step: OnboardingStep): void {
    if (!step.isActionable || !step.route) return;
    void this.router.navigate([step.route]);
  }

  private getBillingAvailabilityForUser(): void {
    this.usersService.isBillingAvailableForUser().subscribe({
      next: isAvailable => this.isBillingAvailableForUser.set(isAvailable),
    });
  }

  private configureSteps(user: AuthUser): void {
    this.steps.update(steps => {
      const updated = [...steps.map(s => ({ ...s }))];
      this.applyAccountActivationStep(updated[0]);
      this.applyCompanyDetailsStep(updated[1], user);
      this.applyLegalDocumentsStep(updated[2], updated[1]);
      return updated;
    });

    if (user.role === Role.MAIN) {
      this.updateLegalDocumentsStepForMainAccount();
    }
  }

  private applyAccountActivationStep(step: OnboardingStep): void {
    const isEnabled = this.usersService.authUser.enabled;
    step.state = isEnabled ? 'done' : 'under-review';
    step.status = isEnabled ? 'Done' : 'Under review';
  }

  private applyCompanyDetailsStep(step: OnboardingStep, user: AuthUser): void {
    const { companyDetailsVerified, addressCity, addressStreet, dataProtectionOfficerEmail } = user;
    step.description =
      'Provide your company and billing details (e.g., address). <strong>Please ensure your details are correct before proceeding.</strong> Invoices will be issued based on this information, and any inaccuracies may lead to processing issues or delays.';

    if (companyDetailsVerified) {
      step.state = 'done';
      step.status = 'Done';
      step.isActionable = false;
      return;
    }

    step.isActionable = true;
    const filled = this.hasValue(addressCity) && this.hasValue(addressStreet) && this.hasValue(dataProtectionOfficerEmail);

    if (filled) {
      step.state = 'under-review';
      step.status = 'Under review';
      step.description =
        "We're currently reviewing your company details. Once the review is complete, you'll be able to review and sign your agreement to start using the platform.";
    } else {
      step.state = 'in-progress';
      step.status = 'To Do';
    }
  }

  private applyLegalDocumentsStep(step: OnboardingStep, companyStep: OnboardingStep, isDone = false): void {
    if (isDone) {
      step.state = 'done';
      step.status = 'Done';
      step.isActionable = false;
      return;
    }

    const prerequisitesMet = companyStep.state === 'done';

    if (prerequisitesMet) {
      step.state = 'in-progress';
      step.status = 'To Do';
      step.isActionable = true;
    } else {
      step.state = 'pending';
      step.status = undefined;
      step.isActionable = false;
    }
  }

  private updateLegalDocumentsStepForMainAccount(): void {
    this.legalDocumentsService.getClickwrapTemplatesForUser().subscribe({
      next: templates => {
        const allAccepted = this.areAllClickwrapTemplatesAccepted(templates);
        this.allClickwrapTemplatesAccepted.set(allAccepted);

        this.steps.update(steps => {
          const updated = [...steps.map(s => ({ ...s }))];
          const companyStep = updated[1];
          const legalDocumentsStep = updated[2];

          this.applyLegalDocumentsStep(legalDocumentsStep, companyStep, allAccepted);
          return updated;
        });
      },
      error: error => {
        console.error('Unable to load legal documents for onboarding.', error);
      },
    });
  }

  private areAllClickwrapTemplatesAccepted(templates: UserClickwrapDetail[]): boolean {
    return templates.length > 0 && templates.every(template => template.status === UserClickwrapStatus.ACCEPTED);
  }

  private hasValue(value: string | null | undefined): boolean {
    return typeof value === 'string' && value.trim().length > 0;
  }
}
